import type { ErrorEvent, StackFrame } from '@sentry/react';

const ERROR_TYPES = new Set(['Error', 'TypeError', 'ReferenceError', 'SyntaxError', 'RangeError', 'URIError', 'EvalError', 'AggregateError']);

export function monitoringSurface(path: string): string {
  if (path.startsWith('/staff')) return 'staff';
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/booking')) return 'booking';
  return 'public';
}

function safeFrame(frame: StackFrame): StackFrame | null {
  try {
    const url = new URL(frame.filename || '', 'https://zavira.ca');
    if (!['zavira.ca', 'www.zavira.ca', 'localhost', '127.0.0.1'].includes(url.hostname)) return null;
    // Only static JS asset locations, never customer URLs, source context or locals.
    if (!/^\/assets\/[\w.-]+\.js$/.test(url.pathname)) return null;
    return {
      filename: `https://zavira.ca${url.pathname}`,
      ...(Number.isInteger(frame.lineno) ? { lineno: frame.lineno } : {}),
      ...(Number.isInteger(frame.colno) ? { colno: frame.colno } : {}),
      in_app: true,
    };
  } catch { return null; }
}

/** Build a fresh allowlisted event; never pass arbitrary SDK/user payloads through. */
export function sanitizeMonitoringEvent(event: ErrorEvent, path: string): ErrorEvent {
  const values = event.exception?.values?.slice(0, 3).map(exception => ({
    type: ERROR_TYPES.has(exception.type || '') ? exception.type : 'Error',
    value: 'Application exception (message omitted for customer privacy)',
    stacktrace: {
      frames: (exception.stacktrace?.frames || []).map(safeFrame).filter((frame): frame is StackFrame => !!frame).slice(-40),
    },
  }));
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: 'javascript',
    level: 'error',
    environment: event.environment === 'production' ? 'production' : 'verification',
    ...(event.release && /^[\w.@+-]{1,100}$/.test(event.release) ? { release: event.release } : {}),
    ...(values?.length ? { exception: { values } } : { message: 'Application error (details omitted for customer privacy)' }),
    tags: { surface: monitoringSurface(path), privacy: 'allowlist-v1' },
    // Explicitly disable Sentry's automatic inference of client IP.
    user: { ip_address: '0.0.0.0' },
  };
}
